import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../../lib/api';
import Chip from '../../../components/Chip';
import EmptyState from '../../../components/EmptyState';
import StatCard, { StatCardRow } from '../../../components/StatCard';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../../../components/StandardTable';
import { HelpTrigger } from '../../../help';
import { formatDate } from '../../lib/format';

type SeatState = 'elect' | 'continuing' | 'seated' | 'awaiting_result' | 'unfilled';

interface CohortSeat {
  office_key: string;
  office: string;
  district_number: number | null;
  district_label: string | null;
  seat_number: number | null;
  seat_state: SeatState;
  person_id: string | null;
  name: string | null;
  party: string | null;
  takes_office: string | null;
  result_status: 'unofficial' | 'certified' | null;
  re_elected: boolean;
}

interface Cohort {
  seats: CohortSeat[];
  counts: Partial<Record<SeatState | 'total', number>>;
}

const STATE_LABEL: Record<SeatState, string> = {
  elect: 'Elect',
  continuing: 'Continuing',
  seated: 'Seated',
  awaiting_result: 'Awaiting result',
  unfilled: 'Unfilled',
};

const STATE_TONE: Record<SeatState, 'positive' | 'info' | 'neutral' | 'caution' | 'negative'> = {
  elect: 'positive',
  continuing: 'info',
  seated: 'neutral',
  awaiting_result: 'caution',
  unfilled: 'negative',
};

const selectClasses = 'input w-auto';

/**
 * Every seat of a cycle's body and who holds it (ELECTION_OUTCOMES_PRD.md §5.14a):
 * winners (Elect), holdovers (Continuing), seats still waiting on a result, and seats
 * with no term on record. The admin's completeness check after a results upload.
 */
const CycleMembersTab: React.FC<{ cycleId: string; started: boolean }> = ({ cycleId, started }) => {
  const [stateFilter, setStateFilter] = useState<SeatState | ''>('');
  const [officeFilter, setOfficeFilter] = useState('');

  const cohort = useQuery({
    queryKey: ['admin-legislature', 'cycle-cohort', cycleId],
    queryFn: async () => {
      const response = await api.get(`/legislature/cycles/${cycleId}/cohort`);
      return response.data.data as Cohort;
    },
  });

  const seats = useMemo(() => cohort.data?.seats ?? [], [cohort.data]);
  const counts = cohort.data?.counts ?? {};
  const offices = useMemo(() => [...new Set(seats.map((s) => s.office))], [seats]);
  const shown = seats.filter((s) => (!stateFilter || s.seat_state === stateFilter) && (!officeFilter || s.office === officeFilter));

  const tile = (state: SeatState, variant: 'success' | 'info' | 'warning' | 'danger' | 'default') => (
    <StatCard label={STATE_LABEL[state]} value={counts[state] ?? 0} variant={variant} loading={cohort.isLoading}
      onClick={() => setStateFilter(stateFilter === state ? '' : state)} />
  );

  if (cohort.isError) return <EmptyState tone="error" title="Couldn't load the members" />;
  if (!cohort.isLoading && seats.length === 0) {
    return <EmptyState title="No seats for this body" hint="Members are listed for the General Assembly and Colorado's delegation to Congress." />;
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
            Members ({counts.total ?? seats.length} seats)
          </h4>
          <HelpTrigger contentKey="legislative-cycles" size="sm" ariaLabel="Who is in a General Assembly or Congress" />
        </span>
        <div className="flex flex-wrap gap-2">
          <select aria-label="Office" className={selectClasses} value={officeFilter} onChange={(e) => setOfficeFilter(e.target.value)}>
            <option value="">All offices</option>
            {offices.map((office) => <option key={office} value={office}>{office}</option>)}
          </select>
          <select aria-label="Seat state" className={selectClasses} value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as SeatState | '')}>
            <option value="">All seats</option>
            {(Object.keys(STATE_LABEL) as SeatState[]).map((state) => (
              <option key={state} value={state}>{STATE_LABEL[state]}</option>
            ))}
          </select>
        </div>
      </div>

      <StatCardRow>
        {started ? tile('seated', 'default') : tile('continuing', 'info')}
        {tile('elect', 'success')}
        {tile('awaiting_result', 'warning')}
        {tile('unfilled', 'danger')}
      </StatCardRow>

      <StandardTable>
        <StandardTableHeader>
          <StandardTableRow>
            <StandardTableHeaderCell>Seat</StandardTableHeaderCell>
            <StandardTableHeaderCell>Member</StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit" responsiveHide="md">Party</StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">Status</StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit" responsiveHide="md">Takes office</StandardTableHeaderCell>
          </StandardTableRow>
        </StandardTableHeader>
        <StandardTableBody>
          {shown.map((seat, index) => (
            <StandardTableRow key={`${seat.office_key}-${seat.district_number ?? ''}-${seat.seat_number ?? ''}-${index}`}>
              <StandardTableCell>
                {seat.office}
                {seat.district_label ? `, ${seat.district_label}` : ''}
                {seat.seat_number ? ` (seat ${seat.seat_number})` : ''}
              </StandardTableCell>
              <StandardTableCell>
                {seat.person_id ? (
                  <Link to={`/admin-next/people/${seat.person_id}`} className="text-primary-600 hover:underline dark:text-primary-400">
                    {seat.name}
                  </Link>
                ) : seat.seat_state === 'unfilled' ? (
                  <span className="text-text-tertiary dark:text-text-dark-tertiary">No term on record: add the holder's term in People</span>
                ) : (
                  <span className="text-text-tertiary dark:text-text-dark-tertiary">—</span>
                )}
                {seat.re_elected && <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">(re-elected)</span>}
              </StandardTableCell>
              <StandardTableCell responsiveHide="md">{seat.party ?? '—'}</StandardTableCell>
              <StandardTableCell>
                <span className="inline-flex flex-wrap gap-1">
                  <Chip label={STATE_LABEL[seat.seat_state]} tone={STATE_TONE[seat.seat_state]} size="sm" />
                  {seat.seat_state === 'elect' && seat.result_status === 'unofficial' && (
                    <Chip label="Unofficial" tone="caution" size="sm" />
                  )}
                </span>
              </StandardTableCell>
              <StandardTableCell responsiveHide="md">{seat.takes_office ? formatDate(seat.takes_office) : '—'}</StandardTableCell>
            </StandardTableRow>
          ))}
        </StandardTableBody>
      </StandardTable>
    </div>
  );
};

export default CycleMembersTab;
